import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  PlayCircle,
  Pencil,
  Trash2,
  Plus,
  GripVertical,
  Package,
  Layers,
  Globe,
  AlertTriangle,
  Check,
  ExternalLink,
} from "lucide-react";
import {
  releasesApi,
  Release,
  ReleaseStep,
  ReleaseEnvironment,
  ReleaseStepType,
} from "../api/client";
import { formatRelativeTime, formatDate, titleCase } from "../lib/utils";

const statusConfig: Record<
  Release["status"],
  { label: string; icon: React.ReactNode; className: string }
> = {
  draft: {
    label: "Draft",
    icon: <FileText className="w-4 h-4" />,
    className: "bg-gray-100 text-gray-700",
  },
  ready: {
    label: "Ready",
    icon: <Clock className="w-4 h-4" />,
    className: "bg-blue-100 text-blue-700",
  },
  in_progress: {
    label: "In Progress",
    icon: <PlayCircle className="w-4 h-4" />,
    className: "bg-yellow-100 text-yellow-700",
  },
  completed: {
    label: "Completed",
    icon: <CheckCircle2 className="w-4 h-4" />,
    className: "bg-green-100 text-green-700",
  },
  cancelled: {
    label: "Cancelled",
    icon: <XCircle className="w-4 h-4" />,
    className: "bg-red-100 text-red-700",
  },
};

const stepTypeLabels: Record<ReleaseStepType, string> = {
  container_deploy: "Container Deploy",
  liquibase: "Liquibase Migration",
  workflow_model: "Workflow Model",
  sql_script: "SQL Script",
  config_change: "Config Change",
  manual_task: "Manual Task",
  curl_command: "cURL Command",
  file_operation: "File Operation",
  service_restart: "Service Restart",
  pre_deployment: "Pre-Deployment",
  post_deployment: "Post-Deployment",
};

const envStatusConfig: Record<
  ReleaseEnvironment["status"],
  { label: string; className: string }
> = {
  pending: { label: "Pending", className: "bg-gray-100 text-gray-700" },
  in_progress: {
    label: "In Progress",
    className: "bg-yellow-100 text-yellow-700",
  },
  completed: { label: "Completed", className: "bg-green-100 text-green-700" },
  failed: { label: "Failed", className: "bg-red-100 text-red-700" },
  skipped: { label: "Skipped", className: "bg-gray-100 text-gray-500" },
};

type TabType = "overview" | "steps" | "artifacts" | "environments";

export default function ReleaseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [showStepForm, setShowStepForm] = useState(false);
  const [editingStep, setEditingStep] = useState<ReleaseStep | null>(null);
  const [showArtifactForm, setShowArtifactForm] = useState(false);

  const { data: release, isLoading: releaseLoading } = useQuery({
    queryKey: ["release", id],
    queryFn: () => releasesApi.get(id!),
    enabled: !!id,
  });

  const { data: stepsData } = useQuery({
    queryKey: ["release-steps", id],
    queryFn: () => releasesApi.listSteps(id!),
    enabled: !!id,
  });

  const { data: artifactsData } = useQuery({
    queryKey: ["release-artifacts", id],
    queryFn: () => releasesApi.listArtifacts(id!),
    enabled: !!id,
  });

  const { data: environmentsData } = useQuery({
    queryKey: ["release-environments", id],
    queryFn: () => releasesApi.listEnvironments(id!),
    enabled: !!id,
  });

  const steps = stepsData?.steps || [];
  const artifacts = artifactsData?.artifacts || [];
  const environments = environmentsData?.environments || [];

  const updateStatusMutation = useMutation({
    mutationFn: (status: "ready" | "cancelled") =>
      releasesApi.updateStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release", id] });
      queryClient.invalidateQueries({ queryKey: ["releases"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => releasesApi.delete(id!),
    onSuccess: () => {
      navigate("/releases");
    },
  });

  const createStepMutation = useMutation({
    mutationFn: (step: Parameters<typeof releasesApi.createStep>[1]) =>
      releasesApi.createStep(id!, step),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-steps", id] });
      setShowStepForm(false);
    },
  });

  const updateStepMutation = useMutation({
    mutationFn: ({
      stepId,
      step,
    }: {
      stepId: string;
      step: Parameters<typeof releasesApi.updateStep>[2];
    }) => releasesApi.updateStep(id!, stepId, step),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-steps", id] });
      setEditingStep(null);
    },
  });

  const deleteStepMutation = useMutation({
    mutationFn: (stepId: string) => releasesApi.deleteStep(id!, stepId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-steps", id] });
    },
  });

  const createArtifactMutation = useMutation({
    mutationFn: (artifact: Parameters<typeof releasesApi.createArtifact>[1]) =>
      releasesApi.createArtifact(id!, artifact),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-artifacts", id] });
      setShowArtifactForm(false);
    },
  });

  const deleteArtifactMutation = useMutation({
    mutationFn: (artifactId: string) =>
      releasesApi.deleteArtifact(id!, artifactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-artifacts", id] });
    },
  });

  const handleStepSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const stepData = {
      step_type: formData.get("step_type") as ReleaseStepType,
      title: formData.get("title") as string,
      description: (formData.get("description") as string) || undefined,
      is_automated: formData.get("is_automated") === "true",
      jenkins_job_name:
        (formData.get("jenkins_job_name") as string) || undefined,
    };

    if (editingStep) {
      updateStepMutation.mutate({ stepId: editingStep.id, step: stepData });
    } else {
      createStepMutation.mutate(stepData);
    }
  };

  const handleArtifactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const artifactData = {
      artifact_name: formData.get("artifact_name") as string,
      artifact_type: formData.get("artifact_type") as string,
      artifact_version: formData.get("artifact_version") as string,
      artifact_url: (formData.get("artifact_url") as string) || undefined,
    };

    createArtifactMutation.mutate(artifactData);
  };

  if (releaseLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (!release) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">
            Release not found
          </h2>
          <button
            onClick={() => navigate("/releases")}
            className="btn btn-primary mt-4"
          >
            Back to Releases
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = statusConfig[release.status];
  const isDraft = release.status === "draft";

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/releases")}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Releases
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {release.title}
              </h1>
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo.className}`}
              >
                {statusInfo.icon}
                {statusInfo.label}
              </span>
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
              <span>
                <strong>Service:</strong> {titleCase(release.service_name)}
              </span>
              <span>
                <strong>Version:</strong>{" "}
                <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                  {release.version}
                </span>
              </span>
              <span>Created {formatRelativeTime(release.created_at)}</span>
            </div>
            {release.description && (
              <p className="text-gray-600 mt-2">{release.description}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isDraft && (
              <>
                <button
                  onClick={() => updateStatusMutation.mutate("ready")}
                  disabled={updateStatusMutation.isPending}
                  className="btn btn-primary"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Mark Ready
                </button>
                <button
                  onClick={() => {
                    if (
                      confirm("Are you sure you want to delete this release?")
                    ) {
                      deleteMutation.mutate();
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="btn btn-secondary text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
            {release.status === "ready" && (
              <button
                onClick={() => navigate(`/releases/${id}/execute`)}
                className="btn btn-primary"
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                Start Execution
              </button>
            )}
            {release.status === "in_progress" && (
              <button
                onClick={() => navigate(`/releases/${id}/execute`)}
                className="btn btn-primary"
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                Continue Execution
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          {[
            { id: "overview", label: "Overview", icon: FileText },
            { id: "steps", label: "Steps", icon: Layers, count: steps.length },
            {
              id: "artifacts",
              label: "Artifacts",
              icon: Package,
              count: artifacts.length,
            },
            {
              id: "environments",
              label: "Environments",
              icon: Globe,
              count: environments.length,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center gap-2 py-3 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "border-brand-600 text-brand-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Release Info Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Release Information
            </h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Service</dt>
                <dd className="text-gray-900 font-medium">
                  {titleCase(release.service_name)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Version</dt>
                <dd className="font-mono bg-gray-100 px-2 py-0.5 rounded text-sm">
                  {release.version}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Status</dt>
                <dd>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusInfo.className}`}
                  >
                    {statusInfo.label}
                  </span>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Created</dt>
                <dd className="text-gray-900">
                  {formatDate(release.created_at)}
                </dd>
              </div>
              {release.created_by_name && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Created By</dt>
                  <dd className="text-gray-900">{release.created_by_name}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Quick Stats Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Stats
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">
                  {steps.length}
                </div>
                <div className="text-sm text-gray-500">Steps</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">
                  {artifacts.length}
                </div>
                <div className="text-sm text-gray-500">Artifacts</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">
                  {environments.length}
                </div>
                <div className="text-sm text-gray-500">Environments</div>
              </div>
            </div>
          </div>

          {/* Environment Progress Card */}
          {environments.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Environment Progress
              </h3>
              <div className="flex items-center gap-2">
                {environments.map((env, index) => (
                  <div key={env.id} className="flex items-center">
                    <div
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        envStatusConfig[env.status].className
                      }`}
                    >
                      {env.environment_name}
                    </div>
                    {index < environments.length - 1 && (
                      <div className="w-8 h-0.5 bg-gray-300 mx-2" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "steps" && (
        <div className="space-y-4">
          {isDraft && (
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setEditingStep(null);
                  setShowStepForm(true);
                }}
                className="btn btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Step
              </button>
            </div>
          )}

          {(showStepForm || editingStep) && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingStep ? "Edit Step" : "Add Step"}
              </h3>
              <form onSubmit={handleStepSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Step Type</label>
                    <select
                      name="step_type"
                      defaultValue={editingStep?.step_type || "manual_task"}
                      className="input w-full"
                      required
                    >
                      {Object.entries(stepTypeLabels).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Title</label>
                    <input
                      type="text"
                      name="title"
                      defaultValue={editingStep?.title}
                      className="input w-full"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Description</label>
                  <textarea
                    name="description"
                    defaultValue={editingStep?.description}
                    className="input w-full"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Automated</label>
                    <select
                      name="is_automated"
                      defaultValue={
                        editingStep?.is_automated ? "true" : "false"
                      }
                      className="input w-full"
                    >
                      <option value="false">No (Manual)</option>
                      <option value="true">Yes (Jenkins)</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Jenkins Job Name</label>
                    <input
                      type="text"
                      name="jenkins_job_name"
                      defaultValue={editingStep?.jenkins_job_name}
                      className="input w-full"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowStepForm(false);
                      setEditingStep(null);
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      createStepMutation.isPending ||
                      updateStepMutation.isPending
                    }
                    className="btn btn-primary"
                  >
                    {editingStep ? "Update" : "Add"} Step
                  </button>
                </div>
              </form>
            </div>
          )}

          {steps.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No steps defined
              </h3>
              <p className="text-gray-600">
                Add deployment steps to this release
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {steps
                .sort((a, b) => a.display_order - b.display_order)
                .map((step, index) => (
                  <div
                    key={step.id}
                    className={`p-4 flex items-center gap-4 ${
                      index > 0 ? "border-t border-gray-200" : ""
                    }`}
                  >
                    <div className="text-gray-400">
                      <GripVertical className="w-5 h-5" />
                    </div>
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">
                      {step.display_order}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {step.title}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                          {stepTypeLabels[step.step_type]}
                        </span>
                        {step.is_automated && (
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                            Automated
                          </span>
                        )}
                      </div>
                      {step.description && (
                        <p className="text-sm text-gray-500 mt-1">
                          {step.description}
                        </p>
                      )}
                    </div>
                    {isDraft && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingStep(step)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm("Delete this step?")) {
                              deleteStepMutation.mutate(step.id);
                            }
                          }}
                          className="p-2 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "artifacts" && (
        <div className="space-y-4">
          {isDraft && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowArtifactForm(true)}
                className="btn btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Artifact
              </button>
            </div>
          )}

          {showArtifactForm && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Add Artifact
              </h3>
              <form onSubmit={handleArtifactSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Name</label>
                    <input
                      type="text"
                      name="artifact_name"
                      className="input w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="label">Type</label>
                    <select
                      name="artifact_type"
                      className="input w-full"
                      required
                    >
                      <option value="container">Container</option>
                      <option value="library">Library</option>
                      <option value="config">Config</option>
                      <option value="database">Database</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Version</label>
                    <input
                      type="text"
                      name="artifact_version"
                      className="input w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="label">URL (optional)</label>
                    <input
                      type="text"
                      name="artifact_url"
                      className="input w-full"
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowArtifactForm(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createArtifactMutation.isPending}
                    className="btn btn-primary"
                  >
                    Add Artifact
                  </button>
                </div>
              </form>
            </div>
          )}

          {artifacts.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No artifacts defined
              </h3>
              <p className="text-gray-600">
                Add components and versions included in this release
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Version
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Validated
                    </th>
                    {isDraft && (
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {artifacts.map((artifact) => (
                    <tr key={artifact.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Package className="w-5 h-5 text-gray-400 mr-3" />
                          <span className="text-sm font-medium text-gray-900">
                            {artifact.artifact_name}
                          </span>
                          {artifact.artifact_url && (
                            <a
                              href={artifact.artifact_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 text-gray-400 hover:text-gray-600"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {artifact.artifact_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">
                          {artifact.artifact_version}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {artifact.is_validated ? (
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-sm">Valid</span>
                          </span>
                        ) : artifact.validation_errors ? (
                          <span className="inline-flex items-center gap-1 text-red-600">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-sm">Invalid</span>
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">Pending</span>
                        )}
                      </td>
                      {isDraft && (
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => {
                              if (confirm("Delete this artifact?")) {
                                deleteArtifactMutation.mutate(artifact.id);
                              }
                            }}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "environments" && (
        <div className="space-y-4">
          {environments.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No environments configured
              </h3>
              <p className="text-gray-600">
                Environments will be assigned when the release is created
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {environments.map((env) => {
                const status = envStatusConfig[env.status];
                return (
                  <div
                    key={env.id}
                    className="bg-white rounded-lg border border-gray-200 p-6"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Globe className="w-6 h-6 text-gray-400" />
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {env.environment_name}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {env.environment_slug}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${status.className}`}
                        >
                          {status.label}
                        </span>
                        {env.status === "pending" &&
                          release.status !== "draft" && (
                            <button
                              onClick={() =>
                                navigate(
                                  `/releases/${id}/execute?env=${env.environment_id}`,
                                )
                              }
                              className="btn btn-primary btn-sm"
                            >
                              <PlayCircle className="w-4 h-4 mr-1" />
                              Start
                            </button>
                          )}
                        {env.status === "in_progress" && (
                          <button
                            onClick={() =>
                              navigate(
                                `/releases/${id}/execute?env=${env.environment_id}`,
                              )
                            }
                            className="btn btn-primary btn-sm"
                          >
                            Continue
                          </button>
                        )}
                      </div>
                    </div>
                    {(env.started_at || env.completed_at) && (
                      <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-500">
                        {env.started_at && (
                          <span>
                            Started: {formatDate(env.started_at)}
                            {env.started_by_name &&
                              ` by ${env.started_by_name}`}
                          </span>
                        )}
                        {env.completed_at && (
                          <span className="ml-4">
                            Completed: {formatDate(env.completed_at)}
                            {env.completed_by_name &&
                              ` by ${env.completed_by_name}`}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
